import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import { Box, Container, TextField, Button, Typography, Alert, AlertTitle } from "@mui/material";
import { registerMember, login, fetchProfileWithToken, ValidateMemberInvitation } from "../services/authAPI";
import { jwtDecode } from "jwt-decode";

function RegisterForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token"); // Extract the token from the URL
  const { setIsLoggedIn, updateTokens, setMember, setIsAdmin, setIsServiceProvider } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    jobTitle: "",
    secondaryEmail: "",
    department: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [tokenValidating, setTokenValidating] = useState(true);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  // Validate token on component mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenInvalid(true);
        setTokenValidating(false);
        return;
      }

      try {
        const response = await ValidateMemberInvitation(token);
        // If token is valid and for existing member, redirect them
        if (response && response.is_existing_member === true) {
          navigate(`/member/register-existing-member?token=${token}`, { replace: true });
          return;
        }
        // Token is valid for new member registration
        setTokenValidating(false);
      } catch (err) {
        console.error("Token validation failed:", err);
        setTokenInvalid(true);
        setTokenValidating(false);
      }
    };

    validateToken();
  }, [token, navigate]);

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push("At least 8 characters");
    }
    if (password.length > 20) {
      errors.push("No more than 20 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("At least one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("At least one lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("At least one number");
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?/~]/.test(password)) {
      errors.push("At least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?/~)");
    }
    // Check for unallowed characters (single quote, double quote, backtick, whitespace)
    if (/['"`\s]/.test(password)) {
      errors.push("Cannot contain quotes, backticks, or spaces");
    }
    
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Validate password in real-time
    if (name === "password") {
      setPasswordErrors(validatePassword(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate password requirements
    const passwordValidationErrors = validatePassword(formData.password);
    if (passwordValidationErrors.length > 0) {
      setError("Password does not meet requirements: " + passwordValidationErrors.join(", "));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      // Step 1: Register the member
      const registrationData = await registerMember({
        token,
        first_name: formData.firstName,
        last_name: formData.lastName,
        secondary_email: formData.secondaryEmail,
        job_title: formData.jobTitle,
        department: formData.department,
        password: formData.password,
      });

      // Step 2: Log the user in with the credentials they just registered
      const loginData = await login(registrationData.member.primaryEmail, formData.password);

      // Extract tokens
      const accessToken = loginData.access_token || loginData.token;
      const refreshToken = loginData.refresh_token || "";
      
      const decodedToken = jwtDecode(accessToken);

      // Step 3: Fetch full member profile
      const memberData = await fetchProfileWithToken(decodedToken.member_id, accessToken);

      // Step 4: Set up the session with the first (and likely only) account
      if (memberData.accounts && memberData.accounts.length > 0) {
        const account = memberData.accounts[0];
        const isServiceProvider = account.account_type === "SERVICE_PROVIDER";
        const isAdmin = !isServiceProvider && 
          account.subscription?.role_code?.toUpperCase() === "ADMIN";
        
        updateTokens(accessToken, refreshToken);
        setMember({...memberData, selected_account: account, acct_id: account.id});
        setIsLoggedIn(true);
        setIsAdmin(isAdmin);
        setIsServiceProvider(isServiceProvider);

        // Save to localStorage
        localStorage.setItem("token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("member", JSON.stringify({...memberData, selected_account: account}));
        localStorage.setItem("isAdmin", JSON.stringify(isAdmin));
        localStorage.setItem("isServiceProvider", JSON.stringify(isServiceProvider));
        localStorage.setItem("isLoggedIn", JSON.stringify(true));

        // Redirect based on account type
        if (isServiceProvider) {
          navigate("/admin");
        } else {
          navigate("/");
        }
      } else {
        throw new Error("No accounts found for the registered user");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Show validating state
  if (tokenValidating) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: "#f5f5f5" }}
      >
        <Container maxWidth="sm">
          <Box sx={{ backgroundColor: "white", borderRadius: 2, p: 4, boxShadow: 2, textAlign: "center" }}>
            <Typography variant="h5" gutterBottom>
              Validating Your Invitation...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we verify your registration link.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  // Show invalid token message
  if (tokenInvalid) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: "#f5f5f5" }}
      >
        <Container maxWidth="sm">
          <Box sx={{ backgroundColor: "white", borderRadius: 2, p: 4, boxShadow: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Unable to Validate Registration Link</AlertTitle>
              We couldn't process your registration invitation at this time.
            </Alert>

            <Typography variant="h6" gutterBottom>
              What happened?
            </Typography>
            <Typography variant="body1" paragraph color="text.secondary">
              Your registration link may have expired, already been used, or you may have already completed your registration.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              What should you do?
            </Typography>
            <Box component="ul" sx={{ pl: 2, color: "text.secondary" }}>
              <li>
                <Typography variant="body1" paragraph>
                  <strong>If you've already registered:</strong> Try logging in with your credentials using the button below.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  <strong>If you haven't registered yet:</strong> Contact the person who sent you the invitation and request a new registration link.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  <strong>Need help?</strong> Reach out to your account administrator to confirm your invitation is still valid.
                </Typography>
              </li>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: 4, flexDirection: { xs: "column", sm: "row" } }}>
              <Button
                component={Link}
                to="/login"
                variant="contained"
                color="primary"
                fullWidth
              >
                Go to Login
              </Button>
              <Button
                component={Link}
                to="/"
                variant="outlined"
                color="primary"
                fullWidth
              >
                Return Home
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box mt={8}>
      <Container maxWidth="sm">
        <Typography variant="h4" component="h1" gutterBottom>
          Register
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <TextField
            label="First Name"
            name="firstName"
            fullWidth
            margin="normal"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          <TextField
            label="Last Name"
            name="lastName"
            fullWidth
            margin="normal"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
          <TextField
            label="Alternate Email"
            name="secondaryEmail"
            fullWidth
            margin="normal"
            value={formData.secondaryEmail}
            onChange={handleChange}
          />
          <TextField
            label="Job Title"
            name="jobTitle"
            fullWidth
            margin="normal"
            value={formData.jobTitle}
            onChange={handleChange}
          />
          <TextField
            label="Department"
            name="department"
            fullWidth
            margin="normal"
            value={formData.department}
            onChange={handleChange}
            autoComplete="off"
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            fullWidth
            margin="normal"
            value={formData.password}
            onChange={handleChange}
            required
            autoComplete="new-password"
            error={formData.password.length > 0 && passwordErrors.length > 0}
            helperText={
              formData.password.length > 0 && passwordErrors.length > 0
                ? passwordErrors.join(", ")
                : "8-20 characters with uppercase, lowercase, number, and special character (!@#$%^&*()_+-=[]{}|;:,.<>?/~). No quotes or spaces."
            }
          />
          <TextField
            label="Confirm Password"
            name="confirmPassword"
            type="password"
            fullWidth
            margin="normal"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
            autoComplete="new-password"
            error={
              formData.confirmPassword.length > 0 &&
              formData.password !== formData.confirmPassword
            }
            helperText={
              formData.confirmPassword.length > 0 &&
              formData.password !== formData.confirmPassword
                ? "Passwords do not match"
                : ""
            }
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={
              loading ||
              passwordErrors.length > 0 ||
              formData.password !== formData.confirmPassword ||
              !formData.password ||
              !formData.confirmPassword
            }
          >
            {loading ? "Registering..." : "Register"}
          </Button>
        </form>
      </Container>
    </Box>
  );
}

export default RegisterForm;